import EventEmitter from 'events';
import Logger from '../Logger';

/**
 * wrapper of UnifiedRecorderValidator
 */
export default class ValidationManagerWrapper extends EventEmitter {
    /**
     * creates instance of ValidationManagerWrapper
     * @param{Stream} stream which should be validated
     * @param{Object.<{
     *    validationCheckInterval: int,
     *    validationRetryCount: int,
     *    validators: Object.<{
     *      activity: Object.<{
     *        checkInterval: int,
     *        }>,
     *      face: Object.<{
     *        checkInterval: int,
     *        }>,
     *      facePosition: Object.<{
     *        checkInterval: int,
     *        }>,
     *      light: Object.<{
     *        checkInterval: int,
     *        }>,
     *    }>,
     *  }>} options for the test and validation, if not provided then defaults will be used.
     * @param{object} unifiedRecorder namespace
     * @param{ConfigurationManager} configurationManager entity to get configuration
     */
    constructor(stream, options, unifiedRecorder, configurationManager) {
        super();
        this.stream = stream;
        this.options = options || {};
        this.environment = options.environment;
        this.logger = new Logger('REC-VSM', configurationManager.isDebugMode());

        this.validationBusyTimeout = undefined;
        this.logger.logDebug(`validation with options: ${JSON.stringify(options)}`);
        const { validation } = unifiedRecorder;
        const validators = [];

        if (this.stream) {
            if (!(this.environment.isSafari && this.environment.isMobile)) {
                if (options.validators.activity) {
                    const activity = new validation.validators.VideoActivityValidator(
                        this.stream,
                        {
                            interval: options.validators.activity.checkInterval,
                        },
                    );
                    validators.push(activity);
                    this.logger.logDebug('activity validator enabled!');
                }

                if (options.validators.faceDetection) {
                    const faceDetection = new validation.validators.FacePositionValidator(
                        this.stream,
                        {
                            interval: options.validators.faceDetection.checkInterval,
                        },
                    );
                    validators.push(faceDetection);
                    this.logger.logDebug('faceDetection validator enabled!');
                }
            }

            this.validationManager = new validation.ValidationManager(
                validators,
                options.validationCheckInterval,
            );

            this.validationManager.validatorsCount = validators.length;
            this.logger.logDebug(`totally ${validators.length} validators added`);
        }

        this.RetryCount = options.validationRetryCount;
    }

    /**
     * process results from ValidationManager and combine it to single object
     * @param{Map} results ValidationManager result object
     * @return{void}
     */
    processValidationCheck(results) {
        this.logger.logDebug(`validator results: ${JSON.stringify(results)}`);
        let ok = true;
        let deviceBusy = false;

        results.forEach((r) => {
            if (r.toLowerCase() !== 'ok') {
                ok = false;
            }

            if (['device_busy'].indexOf(r.toLowerCase()) > -1) {
                deviceBusy = true;
            }
        });

        if (!ok) {
            this.RetryCount -= 1;
            if (this.RetryCount <= 0) {
                this.RetryCount = this.options.validationRetryCount;
                this.emit('test', { ok, deviceBusy, results });
            }
        } else {
            this.emit('test', { ok, deviceBusy, results });
        }
    }

    /**
     * start validation process
     * @return {Promise<boolean>}
     */
    start() {
        if (this.validationManager) {
            this.validationManager.on('results', data => this.processValidationCheck(data));
            if (this.validationManager.validatorsCount === 0) {
                this.validationManager.start = () => {
                    this.validationManager.dummyInt = setInterval(() => {
                        this.processValidationCheck(['ok']);
                    }, this.options.validationCheckInterval || 500);
                };

                this.validationManager.stop = () => {
                    if (this.validationManager.dummyInt) {
                        clearInterval(this.validationManager.dummyInt);
                    }
                };
            }

            this.validationManager.start();
            this.logger.logDebug('validation started!');
        } else {
            // if stream doesn't exist but devices allowed - we should try to re-get it
            this.logger.logDebug('Stream for test dos not exist');

            this.busyFakeInterval = setInterval(() => {
                this.processValidationCheck(['device_busy']);
            }, this.options.validationCheckInterval);
        }
        return Promise.resolve(true);
    }

    /**
     * stops previously launched validation
     * @return {Promise<boolean>}
     */
    stop() {
        if (this.validationManager || this.busyFakeInterval) {
            this.logger.logDebug('test stopped');
            if (this.validationManager) {
                this.validationManager.stop();
                this.validationManager = null;
            }

            if (this.busyFakeInterval) {
                clearInterval(this.busyFakeInterval);
                this.busyFakeInterval = null;
            }
        }

        return Promise.resolve(true);
    }
}
